import React from 'react';
import { ClientUser } from '@he-novation/config/types/client.types';
import { EventUser, HerawEvent, HerawEventProject } from '@he-novation/config/types/event.types';
import { doubleDigit } from '@he-novation/paths/herawFrontUris';
import { addHours, areDatesDifferent, countDifferentDays } from '@he-novation/utils/datetime';
import orderBy from 'lodash/orderBy';
import uniqBy from 'lodash/uniqBy';
import {
    CalendarZoom,
    GlobalRowsType,
    ScrollCalendarGrouper,
    ScrollCalendarItemType,
    ScrollCalendarRowType,
    TimeSlot
} from './ScrollCalendar.types';

import {
    CalendarFluidLabelItem,
    CalendarFluidLabelLabel,
    CalendarFluidLabelUser
} from '$components/ScrollCalendar/CalendarFluidLabels/CalendarFluidLabels';
import { ClientEventLabel } from '$types/clientTypes';
import { ContactType } from '$types/contactTypes';
import { Item } from '$types/items';

type CalendarConfig = {
    [key in CalendarZoom]: {
        columnWidth: number;
        timeSlot?: number[];
    };
};

export const CALENDAR_CONFIG: CalendarConfig = {
    [CalendarZoom.Day]: {
        columnWidth: 720
    },
    [CalendarZoom.Days]: {
        columnWidth: 360,
        timeSlot: [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]
    },
    [CalendarZoom.Weeks]: {
        columnWidth: 120,
        timeSlot: [8, 10, 12, 14, 16, 18, 20]
    },
    [CalendarZoom.Months]: {
        columnWidth: 40,
        timeSlot: [8, 12, 16, 20]
    }
    // [CalendarZoom.Quarters]: {
    //     columnWidth: 16,
    //     timeSlot: [8, 20]
    // }
};

export const CALENDAR_ITEM_HEIGHT = 40;

export const DEFAULT_DAYS_INTERVAL = 30;

export enum DefaultWorkingHours {
    Start = 8,
    End = 20
}

export const OUT_OF_WORKING_HOURS_WIDTH = 10;

export const CALENDAR_ITEMS_TOP_MARGIN = 4;

export const PREVIEW_ITEM_UUID = 'preview-item';

export const computeEventLeftOffset = (
    start: Date,
    end: Date,
    hourWidth: number,
    zoomValue: number,
    timeSlot: TimeSlot,
    duration: number
) => {
    // special case if event starts and ends outside of working hours, ex: thursday 23:00 -  friday 06:00
    // then it should be placed between thursday and friday
    if (isStartsAndEndsOutsideWorkingHours(start, end, timeSlot, duration)) {
        return zoomValue + (OUT_OF_WORKING_HOURS_WIDTH * 2 - 6);
    }

    // if event starts before working hours
    if (start.getHours() < timeSlot.start) {
        return -1;
    }

    if (start.getHours() > timeSlot.end && end.getHours() < 24) {
        return zoomValue - 12;
    }

    const { start: adjustedStart } = getAdjustedWorkingTimesStartAndEndDate(start, end, timeSlot);

    const nbHoursInRange = adjustedStart.getHours() - timeSlot.start;

    return nbHoursInRange * hourWidth + OUT_OF_WORKING_HOURS_WIDTH;
};

export const isStartsAndEndsOutsideWorkingHours = (
    start: Date,
    end: Date,
    timeSlot: TimeSlot,
    duration: number
): boolean => {
    const outsideWorkingHoursDuration = 24 - timeSlot.end + timeSlot.start;
    if (outsideWorkingHoursDuration < duration) {
        return false;
    }
    return (
        start.getHours() > timeSlot.start &&
        end.getHours() < timeSlot.start &&
        countDifferentDays(start.toISOString(), end.toISOString()) === 2
    );
};

export function areOverlapping(
    event1: ScrollCalendarItemType,
    event2: ScrollCalendarItemType
): boolean {
    const start1 = new Date(event1.startDate).getTime();
    const end1 = addHours(event1.startDate, event1.duration).getTime();
    const start2 = new Date(event2.startDate).getTime();
    const end2 = addHours(event2.startDate, event2.duration).getTime();

    return start1 <= end2 && start2 <= end1;
}

export function addLineIndexToEvents(events: ScrollCalendarItemType[]): ScrollCalendarItemType[] {
    //sort events by startDate
    events.sort((a, b) => {
        return a.startDate.getTime() - b.startDate.getTime();
    });

    const lines: Date[] = [];

    events.forEach((event) => {
        const eventStart = event.startDate;
        const eventEnd = addHours(event.startDate, event.duration);
        let placed = false;

        for (let i = 0; i < lines.length; i++) {
            if (eventStart >= lines[i]) {
                // If the event doesn't overlap, place it on this line
                event.level = i;
                lines[i] = eventEnd;
                placed = true;
                break;
            }
        }

        // If it couldn't be placed on any existing line, create a new line
        if (!placed) {
            event.level = lines.length;
            lines.push(eventEnd);
        }
    });

    return events;
}

export function groupOverlappingEvents(
    events: ScrollCalendarItemType[]
): ScrollCalendarItemType[][] {
    const groupedEvents: ScrollCalendarItemType[][] = [];

    events.forEach((event) => {
        let added = false;

        // Check if the event overlaps with any existing group
        for (const group of groupedEvents) {
            if (group.some((groupEvent) => areOverlapping(groupEvent, event))) {
                group.push(event);
                added = true;
                break;
            }
        }

        // If the event didn't match any existing group, create a new group
        if (!added) {
            groupedEvents.push([event]);
        }
    });

    return groupedEvents;
}

type ComputeEventWidthType = {
    start: Date;
    hourWidth: number;
    columnWidth: number;
    isStartEarlierThanWorkingHours: boolean;
    item: ScrollCalendarItemType;
    timeSlot: TimeSlot;
};

export const computeEventWidth = ({
    start,
    hourWidth,
    columnWidth,
    isStartEarlierThanWorkingHours,
    item,
    timeSlot
}: ComputeEventWidthType): number => {
    const end = addHours(item.startDate, item.duration);
    const isSeveralDays = areDatesDifferent(start, end);

    if (item?.isTask) {
        return columnWidth - OUT_OF_WORKING_HOURS_WIDTH;
    }

    if (isStartEarlierThanWorkingHours && !isSeveralDays) {
        const startHour = item.startDate.getHours();
        const hoursBeforeWorkingHours = timeSlot.start - startHour;
        const durationInWorkingHours = item.duration - hoursBeforeWorkingHours;
        return Math.max(10, durationInWorkingHours * hourWidth + OUT_OF_WORKING_HOURS_WIDTH);
    }

    const hours = item.duration;
    const additionalWidth = isStartEarlierThanWorkingHours ? OUT_OF_WORKING_HOURS_WIDTH : 0;

    // if the event spans on multiple days we need to compute the numbers of hours that are not in the working hours
    // and subtract them from the total hours

    // check if the start date and end date are not on the same day
    if (isSeveralDays) {
        const nbOfDays = countDifferentDays(start.toISOString(), end.toISOString());
        const nbHoursOfficeOnFirstDay = timeSlot.end - start.getHours();
        const nbHoursOfficeOnLastDay = Math.max(
            Math.min(end.getHours() - timeSlot.start, timeSlot.end - timeSlot.start),
            0
        );
        const inOfficeHoursPerDay = timeSlot.end - timeSlot.start;

        const inOfficeHoursBetweenStartAndEnd = (nbOfDays - 2) * inOfficeHoursPerDay;
        const inOfficeHours =
            nbHoursOfficeOnFirstDay + nbHoursOfficeOnLastDay + inOfficeHoursBetweenStartAndEnd;

        const inOfficeWidth = inOfficeHours * hourWidth;
        const outOfOfficeWidth = (nbOfDays - 1) * (OUT_OF_WORKING_HOURS_WIDTH * 2);

        return inOfficeWidth + outOfOfficeWidth + additionalWidth;
    }
    // if the event end between the end of the working hours and the end of the day
    if (end.getHours() > timeSlot.end) {
        return (timeSlot.end - start.getHours()) * hourWidth;
    }

    return Math.max(10, hours * hourWidth + additionalWidth);
};

export const getEventDuration = (
    startDate: Date,
    endDate: Date,
    width: number,
    hourWidth: number,
    timeSlot: TimeSlot
): number => {
    if (areDatesDifferent(startDate, endDate)) {
        const absWidth = Math.abs(width);
        const nbOfDays = countDifferentDays(startDate.toISOString(), endDate.toISOString());

        const outOfOfficeHoursLength = (nbOfDays - 1) * 2 * OUT_OF_WORKING_HOURS_WIDTH;
        const outOfOfficeHoursDuration = (nbOfDays - 1) * (24 - timeSlot.end + timeSlot.start);

        const workingHoursLength = absWidth - outOfOfficeHoursLength;
        const workingHoursDuration = workingHoursLength / hourWidth;

        const duration = outOfOfficeHoursDuration + workingHoursDuration;

        return startDate < endDate ? duration : -duration;
    }
    const duration = Math.round(width / hourWidth);
    return duration;
};

export const getAdjustedWorkingTimesStartAndEndDate = (
    start: Date,
    end: Date,
    timeSlot: TimeSlot
) => {
    if (start.getHours() < timeSlot.start) {
        start = new Date(new Date(start).setHours(timeSlot.start, 0, 0, 0));
    }
    if (end.getHours() > timeSlot.end) {
        end = new Date(new Date(end).setHours(timeSlot.end, 0, 0, 0));
    }
    return { start, end };
};

export const getHoursBetweenDates = (date1: Date, date2: Date): number => {
    // Convert both dates to milliseconds and get the time difference
    const timeDifferenceInMilliseconds = date2.getTime() - date1.getTime();

    // Convert milliseconds to hours
    const millisecondsPerHour = 1000 * 60 * 60;
    const hoursDifference = timeDifferenceInMilliseconds / millisecondsPerHour;

    // Round the result to 3 decimal places
    return parseFloat(hoursDifference.toFixed(3));
};

export const formatHeadDate = (date: string, locale: string) => {
    const d = new Date(date);
    return new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        day: '2-digit',
        month: 'short'
    })
        .format(new Date(d))
        .replace(',', '.');
};

export const formatHeadDateForMonth = (date: string, locale: string) => {
    const d = new Date(date);
    return new Intl.DateTimeFormat(locale, {
        day: '2-digit'
    }).format(new Date(d));
};

type CalendarItemsToRowsMapperType = {
    items: HerawEvent[];
    grouper: ScrollCalendarGrouper;
    searchTerm: string;
    globalRows?: GlobalRowsType;
    grouperFilter?: string[];
    currentUserUuid: string;
};

export const mapCalendarItemsToRows = ({
    items,
    grouper,
    searchTerm,
    grouperFilter,
    globalRows,
    currentUserUuid
}: CalendarItemsToRowsMapperType): ScrollCalendarRowType[] => {
    let group: ScrollCalendarRowType[] = [];
    switch (grouper) {
        case ScrollCalendarGrouper.Users: {
            const users = uniqBy(items.map((item) => item?.users).flat(), (item) => item?.uuid);
            if (globalRows?.users) {
                const globalUsersConverted = globalRows?.users.map((user) =>
                    convertGlobalUserToEventUser(user)
                );
                users.push(...globalUsersConverted);
            }
            const uniqUsers = uniqBy(users, (user) => user?.uuid).filter((user) => !!user?.uuid);
            const groupUsers = uniqUsers
                .map((user) => {
                    return {
                        filterString: user
                            ? user.firstname && user.lastname
                                ? `${user.firstname} ${user.lastname}`
                                : user.email
                            : '',
                        label: (
                            <CalendarFluidLabelUser
                                {...user}
                                isCurrentUser={user.uuid === currentUserUuid}
                            />
                        ),
                        items: items.filter(
                            (item) => item?.users?.some((u) => u?.uuid === user?.uuid)
                            // || item?.owner?.uuid === user.uuid // owner
                        ),
                        filterGrouper: user?.labels?.map((label) => label?.uid),
                        data: {
                            users: [user]
                        }
                    };
                })
                .filter((g) => globalRows ?? g.items.length > 0);
            const groupUnassigned = {
                filterString: 'Unassigned',
                label: 'Unassigned',
                unAssigned: true,
                items: items.filter((item) => item?.users?.length === 0),
                data: {
                    users: []
                }
            };

            group = [...groupUsers, groupUnassigned];
            break;
        }
        case ScrollCalendarGrouper.Items: {
            const calendarItems = uniqBy(
                items?.map((item) => item?.items).flat(),
                (item) => item?.uuid
            );
            if (globalRows?.items) {
                const globalItems = globalRows?.items;
                calendarItems.push(...(globalItems as any));
            }
            const uniqItems = uniqBy(calendarItems, (item) => item.uuid);
            const groupItems = uniqItems
                .map((item) => {
                    return {
                        filterString: item.name ?? '',
                        label: <CalendarFluidLabelItem item={item as Item} />,
                        items: items.filter((i) => i.items.some((it) => it.uuid === item.uuid)),
                        data: {
                            items: [item as Item]
                        },
                        filterGrouper: item.labels.map((label) => label.uid)
                    };
                })
                .filter((g) => globalRows ?? g.items.length > 0);
            const groupUnassigned = {
                unAssigned: true,
                filterString: 'Unassigned',
                label: 'Unassigned',
                items: items.filter((item) => item?.items?.length === 0),
                data: {
                    items: []
                }
            };
            group = [...groupItems, groupUnassigned];
            break;
        }
        case ScrollCalendarGrouper.Labels: {
            const calendarLabels = uniqBy(
                items.map((item) => item?.labels)?.flat(),
                (item) => item?.uid
            );
            if (globalRows?.labels) {
                const globalLabels = globalRows.labels;
                calendarLabels.push(...(globalLabels as ClientEventLabel[]));
            }
            const uniqLabels = uniqBy(calendarLabels, (label) => label?.uid);
            const groupLabels = uniqLabels
                ?.map((label: ClientEventLabel) => {
                    return {
                        filterString: label?.name || '',
                        label: label?.name && (
                            <CalendarFluidLabelLabel {...label} name={label.name} />
                        ),
                        items: items?.filter((i) => i?.labels?.some((l) => l?.uid === label?.uid)),
                        data: {
                            labels: [label]
                        }
                    };
                })
                .filter((g) => g.items.length > 0);
            const groupUnassigned = {
                unAssigned: true,
                filterString: 'Unassigned',
                label: 'Unassigned',
                items: items.filter((item) => item?.labels?.length === 0),
                data: {
                    labels: []
                }
            };
            group = [...groupLabels, groupUnassigned];
            break;
        }
        case ScrollCalendarGrouper.Projects: {
            const calendarProjects = uniqBy(
                items?.map((item) => item?.project)?.flat(),
                (item) => item?.uuid
            );
            if (globalRows?.projects) {
                const globalProjects = globalRows.projects;
                calendarProjects.push(...(globalProjects as HerawEventProject[]));
            }
            const uniqProjects = uniqBy(calendarProjects, (project) => project?.uuid).filter(
                (p) => p?.uuid
            );
            const groupProjects = uniqProjects.map((project) => {
                return {
                    filterString: project?.name ?? '',
                    label: project?.name ?? '',
                    items: items.filter((i) => i?.project?.uuid === project?.uuid),
                    data: project ? { project: project } : null
                };
            });
            const groupUnassigned = {
                unAssigned: true,
                filterString: 'Unassigned',
                label: 'Unassigned',
                items: items.filter((item) => !item?.project),
                data: null
            };

            group = [...groupProjects, groupUnassigned];
            break;
        }
    }
    const searchedGroup = group?.filter((g) =>
        removeSpecialCharacters(g?.filterString)
            ?.toLowerCase()
            ?.includes(removeSpecialCharacters(searchTerm)?.toLowerCase())
    );

    const searchedGroupFiltered = searchedGroup?.filter((g) => {
        if (!grouperFilter) return true;
        if (grouperFilter.length === 0) return true;
        return g.filterGrouper?.some((uid) => grouperFilter.includes(uid));
    });

    const orderedGroup = orderBy(
        orderBy(searchedGroupFiltered, (g) => g?.filterString?.toLowerCase(), 'asc'),
        (g) => g?.unAssigned,
        'desc'
    );

    if (grouper === ScrollCalendarGrouper.Users) {
        const currentUserRow = orderedGroup.find((group) => {
            if (group?.data && 'users' in group.data) {
                const groupUsers = group.data.users;
                return groupUsers?.find((user) => user?.uuid === currentUserUuid);
            }
            return false;
        });
        if (currentUserRow) {
            const index = orderedGroup.indexOf(currentUserRow);
            orderedGroup.splice(index, 1);
            orderedGroup.unshift(currentUserRow);
        }
    }
    return orderedGroup;
};

export const getPreviewItem = ({
    startDate,
    endDate,
    duration,
    data,
    ...rest
}): ScrollCalendarItemType => {
    return {
        uuid: PREVIEW_ITEM_UUID,
        color: null,
        title: '',
        description: null,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        duration: duration,
        intervalUnit: 'DAY',
        intervalValue: 0,
        hasItems: true,
        days: {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true
        },
        deletedDays: [],
        client: {
            uuid: 'uuid',
            name: ''
        },
        project: {},
        items: [],
        users: [],
        labels: [],
        teams: [],
        reminders: [],
        canEditAndDelete: true,
        ...rest,
        ...data
    };
};

export const getTimeSlotsHours = (
    timeSlot: { start: number; end: number },
    dayWidth: number
): number[] => {
    const { start, end } = timeSlot;
    const hourWidth = 20; // Each hour takes 20px in width

    const totalHours = end - start;
    const maxVisibleHours = Math.floor(dayWidth / hourWidth); // Maximum hours that can fit in the container

    // Ensure we always have at least 2 visible hours (start and end)
    if (maxVisibleHours < 2) return [start, end];

    // Calculate a temporary interval
    let interval = Math.ceil(totalHours / (maxVisibleHours - 1));

    // Adjust the interval so the end hour is always included while keeping spacing even
    while (Math.ceil((end - start) / interval) + 1 > maxVisibleHours) {
        interval++;
    }

    const result: number[] = [];

    // Push the start hour and increment by the adjusted interval
    for (let hour = start; hour <= end; hour += interval) {
        result.push(hour);
    }

    // Ensure the end hour is included without breaking the interval consistency
    if (result[result.length - 1] !== end) {
        result.push(end);
    }

    return result;
};

export const removeSpecialCharacters = (str: string): string => {
    return str
        .normalize('NFD') // Normalize to decomposed form
        .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
        .replace(/[^a-zA-Z0-9\s]/g, ''); // Remove special characters
};

export const isElementInViewport = (query: string): boolean => {
    // Select the element using the query string
    const element = document.querySelector(query) as HTMLElement | null;

    if (!element) return false;

    const rect = element.getBoundingClientRect();

    // Check if the element is fully within the viewport bounds
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
};

export const trackMultipleVisibleElements = (
    attributes: string[], // Multiple attributes to track
    onTrack: (elements: { [key: string]: string[] }) => void,
    {
        rootMargin,
        rootRef,
        threshold = 0
    }: {
        rootMargin?: string;
        rootRef?: React.RefObject<Element>;
        threshold?: number;
    } = {}
) => {
    const visibleElements: { [key: string]: Set<string> } = {};

    attributes.forEach((attr) => {
        visibleElements[attr] = new Set();
    });

    const allElements = attributes.flatMap((attr) =>
        Array.from(document.querySelectorAll(`[${attr}]`))
    );

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                attributes.forEach((attr) => {
                    const element = (entry.target as HTMLElement).getAttribute(attr);

                    if (element) {
                        if (entry.isIntersecting) {
                            visibleElements[attr].add(element);
                        } else {
                            visibleElements[attr].delete(element);
                        }
                    }
                });
                // Aggregate all visible elements
                const visibleElementsArray = Object.keys(visibleElements).reduce((acc, key) => {
                    acc[key] = Array.from(visibleElements[key]);
                    return acc;
                }, {} as { [key: string]: string[] });
                onTrack(visibleElementsArray);
            });
        },
        {
            root: rootRef?.current,
            threshold: threshold ?? 0,
            rootMargin: rootMargin ?? '90px 2000px 90px 2000px'
        }
    );

    allElements.forEach((element) => observer.observe(element));

    return () => {
        observer.disconnect();
    };
};

const convertGlobalUserToEventUser = (user: ClientUser | ContactType): EventUser => {
    return {
        ...user,
        locale: '',
        timezone: ''
    } as EventUser;
};

export const roundToNearestQuarter = (value: number): number => Math.round(value * 4) / 4;

export const renderUserName = (user: EventUser): string => {
    if (user.lastname && user.firstname) {
        return `${user.firstname} ${user.lastname}`;
    }
    return user.email;
};

export const formatDateQuery = (date: Date) =>
    `${date.getFullYear()}-${doubleDigit(date.getMonth() + 1)}-${doubleDigit(date.getDate())}`;

export const setAnchor = (date: Date) => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set(
        'start',
        `${date.getFullYear()}-${doubleDigit(date.getMonth() + 1)}-${doubleDigit(date.getDate())}`
    );
    window.history.replaceState({}, '', window.location.pathname + '?' + queryParams.toString());
};
